import { canStartTool, toolDefinitions } from "../../features/tools/registry";
import { OPEN_ASSISTANT_EVENT } from "../../features/toolExperience/assistantSurface";
import { selectHomeMode } from "./homeState";
import ContinueWork from "./ContinueWork";
import NeedsAttention from "./NeedsAttention";
import RecoveryNotice from "../../components/RecoveryNotice/RecoveryNotice";
import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowRight,
  Check,
  FileText,
  Layers,
  MessagesSquare,
  ScanEye,
  Sparkles,
  X,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useOrganization } from "../../contexts/OrganizationContext";
import { useAgentContext } from "../../contexts/AgentContext";
import { useDashboardData } from "../../hooks/useDashboardData";
import { useBilling } from "../../hooks/useBilling";
import { useToast } from "../../hooks/useToast";
import { authAPI } from "../../api/auth";
import { listApprovals } from "../../api/approvals";
import ChatInterface from "../../components/AgentChat/ChatInterface";
import { getUpgradeCta } from "./upgradeCta";
import GuidedStart from "./GuidedStart";
import {
  buildGuidedPrompt,
  emptyOnboarding,
  hasMonitoringResult,
  readOnboarding,
  saveOnboarding,
  trackOnboarding,
  type GuidedTask,
  type OnboardingState,
} from "./onboarding";
import styles from "./Dashboard.module.css";

export default function Dashboard() {
  const { user, isLoading } = useAuth();
  const { currentOrganization } = useOrganization();
  if (isLoading || !user || !currentOrganization)
    return <p role="status">Preparing your workspace…</p>;
  return (
    <DashboardContent
      key={`${user.id}:${currentOrganization.id}`}
      userId={user.id}
      orgId={currentOrganization.id}
    />
  );
}

function DashboardContent({
  userId,
  orgId,
}: {
  userId: string;
  orgId: string;
}) {
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();
  const agent = useAgentContext();
  const clustering = toolDefinitions.find((tool) => tool.id === "clustering")!;
  const queryClient = useQueryClient();
  const data = useDashboardData();
  const { billing, loading: billingLoading } = useBilling(orgId);
  const toast = useToast();
  const [draft, setDraft] = useState(() => readOnboarding(userId, orgId));
  const [showGuide, setShowGuide] = useState(
    () => !!draft.task && !draft.submittedAt,
  );
  const [sending, setSending] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const sendLock = useRef(false);
  const chatRef = useRef<HTMLDivElement>(null);
  const { data: approvals = [] } = useQuery({
    queryKey: ["approvals", userId, orgId],
    queryFn: () => listApprovals(),
    refetchInterval: 30_000,
  });
  const busy = sending || restoring || agent.isThinking || !!agent.pendingPlan;
  const upgrade = getUpgradeCta(billing, billingLoading);
  const [assistantExpanded, setAssistantExpanded] = useState(false);
  const hasConversation =
    agent.messages.length > 0 || !!agent.pendingPlan || agent.isThinking;
  const homeMode = selectHomeMode({
    sources: data.sources,
    onboarding: {
      dismissed: draft.dismissed,
      completed: !!draft.resultAt,
      hasDraft: !!draft.task,
    },
    hasConversation,
  });
  useEffect(() => {
    if (hasConversation || showGuide || homeMode === "guided")
      setAssistantExpanded(true);
  }, [hasConversation, showGuide, homeMode]);
  useEffect(() => {
    const open = () => {
      setAssistantExpanded(true);
      requestAnimationFrame(() => {
        chatRef.current?.scrollIntoView({ block: "start" });
        chatRef.current?.querySelector("textarea")?.focus();
      });
    };
    window.addEventListener(OPEN_ASSISTANT_EVENT, open);
    return () => window.removeEventListener(OPEN_ASSISTANT_EVENT, open);
  }, []);
  const { setAssistantVisible } = agent;
  useEffect(() => {
    setAssistantVisible(assistantExpanded);
    return () => setAssistantVisible(false);
  }, [assistantExpanded, setAssistantVisible]);
  const latestSession = agent.sessions[0]; // Session API is scoped by the authenticated organization.

  const updateDraft = useCallback(
    (change: Partial<OnboardingState>) =>
      setDraft((value) => {
        const next = { ...value, ...change };
        saveOnboarding(userId, orgId, next);
        return next;
      }),
    [userId, orgId],
  );

  useEffect(() => {
    if (
      agent.isThinking ||
      agent.error ||
      agent.messages[agent.messages.length - 1]?.type === "AGENT"
    ) {
      setSending(false);
      sendLock.current = false;
    }
  }, [agent.isThinking, agent.error, agent.messages]);

  // Only server-backed results count as completed work.
  const wasThinking = useRef(false);
  useEffect(() => {
    if (wasThinking.current && !agent.isThinking)
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    wasThinking.current = agent.isThinking;
  }, [agent.isThinking, queryClient]);
  useEffect(() => {
    if (!draft.task || !draft.submittedAt || draft.resultAt) return;
    const after = draft.submittedAt;
    const result =
      draft.task === "briefing"
        ? data.completedBriefs.some(
            (brief) =>
              Date.parse(
                brief.completed_at || brief.updated_at || brief.created_at,
              ) >= after &&
              brief.input_data.keyword?.trim().toLowerCase() ===
                draft.keyword.trim().toLowerCase() &&
              (!draft.projectId || brief.project_id === draft.projectId),
          )
        : hasMonitoringResult(draft, data.monitoringProjects);
    if (result) {
      const resultAt = Date.now();
      updateDraft({ resultAt });
      trackOnboarding(
        "first_result",
        draft.task,
        (resultAt - (draft.startedAt || after)) / 1000,
      );
    }
  }, [data.completedBriefs, data.monitoringProjects, draft, updateDraft]);

  const choose = (task: GuidedTask) => {
    if (busy) return;
    const next =
      draft.task === task
        ? draft
        : {
            ...emptyOnboarding(),
            dismissed: draft.dismissed,
            resultAt: draft.resultAt,
          };
    updateDraft({ ...next, task, startedAt: next.startedAt || Date.now() });
    setShowGuide(true);
    trackOnboarding("task_selected", task);
  };
  const send = (content: string) => {
    if (busy || sendLock.current) return;
    sendLock.current = true;
    setSending(true);
    setShowGuide(false);
    agent.sendMessage(content);
  };
  const prepare = () => {
    if (busy || sendLock.current || !draft.task) return;
    updateDraft({ submittedAt: Date.now() });
    trackOnboarding("details_supplied", draft.task);
    send(
      buildGuidedPrompt(
        draft,
        data.briefingProjects.find((project) => project.id === draft.projectId)
          ?.name,
      ),
    );
    requestAnimationFrame(() =>
      chatRef.current?.scrollIntoView({ block: "start", behavior: "smooth" }),
    );
  };
  const resendVerification = async () => {
    setVerifying(true);
    try {
      await authAPI.resendVerification(user!.email);
      toast.success("Verification email sent. Please check your inbox.");
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Could not send verification email.",
      );
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className={styles.dashboard}>
      <header className={styles.welcome}>
        <div>
          <p className={styles.eyebrow}>{currentOrganization?.name}</p>
          <p className={styles.greeting}>
            Welcome back, {user?.name.split(" ")[0]}{" "}
            <span aria-hidden="true">👋</span>
          </p>
        </div>
        <div className={styles.accountMeta}>
          <span>{billing?.plan.name || "Your workspace"}</span>
          {upgrade.visible && (
            <Link to="/admin/billing/upgrade">
              {upgrade.creditsLabel ? `${upgrade.creditsLabel} · ` : ""}Upgrade
            </Link>
          )}
        </div>
      </header>
      {!user?.emailVerified && (
        <div className={styles.notice}>
          Verify your email to access all features.
          <button
            type="button"
            onClick={resendVerification}
            disabled={verifying}
          >
            {verifying ? "Sending…" : "Resend verification"}
          </button>
        </div>
      )}

      <h1 className={styles.homeTitle}>Home</h1>
      {homeMode === "loading" && <p role="status">Loading your work…</p>}
      {data.errors.length > 0 && (
        <RecoveryNotice
          onRetry={data.retrySources}
          action={<Link to="/tools">Open tools</Link>}
        >
          Some updates are unavailable. Your tools are still available.
        </RecoveryNotice>
      )}
      <NeedsAttention items={data.workFeed} approvals={approvals.length} />
      {homeMode === "returning" && <ContinueWork items={data.workFeed} />}
      <details
        className={styles.hero}
        open={assistantExpanded}
        onToggle={(e) => setAssistantExpanded(e.currentTarget.open)}
      >
        <summary className={styles.assistantSummary}>
          Ask assistant <span>Get help starting or continuing a task</span>
        </summary>
        <div className={styles.heroIntro}>
          <span className={styles.agentMark}>
            <Sparkles size={26} aria-hidden="true" />
          </span>
          <div>
            <p className={styles.eyebrow}>Your LazySEM assistant</p>
            <h2 id="dashboard-title">What would you like to work on today?</h2>
            <p className={styles.subtitle}>
              Choose a starting point, or describe what you need. I’ll guide you
              through it.
            </p>
          </div>
        </div>
        {!showGuide && (
          <div className={styles.choices}>
            <button
              className={styles.choice}
              aria-label="Create a content brief"
              disabled={busy}
              onClick={() => choose("briefing")}
            >
              <FileText aria-hidden="true" />
              <strong>Create a content brief</strong>
              <span>Research a keyword and prepare your next article.</span>
              <ArrowRight
                className={styles.choiceArrow}
                size={18}
                aria-hidden="true"
              />
            </button>
            <button
              className={styles.choice}
              aria-label="Monitor a website"
              disabled={busy}
              onClick={() => choose("monitoring")}
            >
              <ScanEye aria-hidden="true" />
              <strong>Monitor a website</strong>
              <span>Keep track of changes to important pages.</span>
              <ArrowRight
                className={styles.choiceArrow}
                size={18}
                aria-hidden="true"
              />
            </button>
            <Link
              className={styles.choice}
              to={
                canStartTool(clustering, user?.role)
                  ? clustering.startAction!.href
                  : clustering.path
              }
            >
              <Layers aria-hidden="true" />
              <strong>Organize my keywords</strong>
              <span>Group related keywords into useful topics.</span>
              <ArrowRight
                className={styles.choiceArrow}
                size={18}
                aria-hidden="true"
              />
            </Link>
            <Link className={styles.choice} to="/similarity?start=comparison">
              <MessagesSquare aria-hidden="true" />
              <strong>Compare content</strong>
              <span>Find similar pages and potential overlap.</span>
              <ArrowRight
                className={styles.choiceArrow}
                size={18}
                aria-hidden="true"
              />
            </Link>
          </div>
        )}
        {showGuide && draft.task && (
          <GuidedStart
            draft={draft}
            projects={data.briefingProjects}
            busy={busy}
            onChange={updateDraft}
            onSubmit={prepare}
            onClose={() => setShowGuide(false)}
          />
        )}
        <div className={styles.assistant} ref={chatRef}>
          <div className={styles.sectionHeading}>
            <h2>
              {hasConversation
                ? "Your conversation"
                : "Or tell me what you have in mind"}
            </h2>
            <Link to="/agent">
              Open full assistant <ArrowRight size={14} aria-hidden="true" />
            </Link>
          </div>
          <ChatInterface
            unsentRequest={agent.unsentRequest}
            onClearUnsentRequest={agent.clearUnsentRequest}
            conversationKey={`${agent.scopeKey}:${agent.currentSessionId}`}
            draft={{
              value: agent.draftMessage,
              onChange: agent.setDraftMessage,
            }}
            messages={agent.messages}
            isThinking={agent.isThinking || sending || restoring}
            error={agent.error}
            pendingPlan={agent.pendingPlan}
            planActionsDisabled={!agent.isConnected}
            onSend={send}
            onApprove={agent.approvePlan}
            onReject={agent.rejectPlan}
            embedded
            emptyState={null}
            inputPlaceholder="e.g. Help me prepare a brief about running shoes"
            className={`${styles.chat} ${hasConversation ? styles.chatActive : styles.chatEmpty}`}
          />
          <div className={styles.assistantActions}>
            {agent.pendingPlan && !agent.isConnected && (
              <button
                type="button"
                className={styles.textButton}
                disabled={restoring}
                onClick={async () => {
                  if (!agent.currentSessionId) return;
                  setRestoring(true);
                  try {
                    await agent.selectSession(agent.currentSessionId);
                  } finally {
                    setRestoring(false);
                  }
                }}
              >
                Reconnect assistant
              </button>
            )}
            <button
              type="button"
              className={styles.textButton}
              disabled={busy}
              onClick={() =>
                send(
                  "I’m new to LazySEM. Help me choose between creating a content brief, monitoring a page, organizing keywords, and comparing content. Ask me one simple question about my goal first. Do not run any tools or paid actions yet.",
                )
              }
            >
              Help me choose
            </button>
            {draft.task && !showGuide && (
              <button
                type="button"
                className={styles.textButton}
                disabled={busy}
                onClick={() => setShowGuide(true)}
              >
                Resume saved{" "}
                {draft.task === "briefing" ? "brief" : "monitoring"} setup
              </button>
            )}
            {latestSession && !agent.currentSessionId && (
              <button
                type="button"
                className={styles.textButton}
                disabled={busy}
                onClick={async () => {
                  setRestoring(true);
                  try {
                    await agent.selectSession(latestSession.session_id);
                  } finally {
                    setRestoring(false);
                  }
                }}
              >
                Continue last conversation
              </button>
            )}
          </div>
          {agent.error && (
            <p className={styles.notice}>
              You can continue using{" "}
              <Link to="/briefings?start=brief">Briefings</Link> or{" "}
              <Link to="/monitoring?create=1">Monitoring</Link> directly.
            </p>
          )}
        </div>
      </details>

      {!data.isLoading &&
        !draft.dismissed &&
        (homeMode === "guided" || !!draft.task) && (
          <section className={styles.checklist} aria-label="Getting started">
            <div className={styles.sectionHeading}>
              <h2>
                {draft.resultAt
                  ? "Your first result is ready"
                  : "Your first useful result"}
              </h2>
              <button
                type="button"
                aria-label="Dismiss getting started"
                className={styles.iconButton}
                onClick={() => updateDraft({ dismissed: true })}
              >
                <X size={18} />
              </button>
            </div>
            <ol>
              {[
                ["Choose a task", !!draft.task],
                ["Provide the essentials", !!draft.submittedAt],
                ["Get your first result", !!draft.resultAt],
              ].map(([label, done], i) => (
                <li key={String(label)} className={done ? styles.stepDone : ""}>
                  <span className={styles.stepNumber}>
                    {done ? <Check size={15} aria-hidden="true" /> : i + 1}
                  </span>
                  <span>
                    {label}
                    {done && <span className={styles.srOnly}> — complete</span>}
                  </span>
                </li>
              ))}
            </ol>
            {draft.submittedAt && !draft.resultAt && (
              <p className={styles.small}>
                Your setup is saved. Continue the conversation to review the
                plan and follow progress.
              </p>
            )}
          </section>
        )}

      <details className={styles.overview}>
        <summary>Workspace overview</summary>
        <div className={styles.stats}>
          {data.counts.map(({ label, value }) => (
            <div key={label}>
              <strong>{value === null ? "—" : value}</strong>
              <span>{label}</span>
            </div>
          ))}
        </div>
        <div className={styles.assistantActions}>
          <Link to="/briefings">All briefings</Link>
          <Link to="/monitoring">All monitoring</Link>
          {draft.dismissed && (
            <button
              type="button"
              className={styles.textButton}
              onClick={() => updateDraft({ dismissed: false })}
            >
              Show getting started
            </button>
          )}
        </div>
      </details>
    </div>
  );
}
